/**
 * VisionPostProcess — chromatic-aberration + sepia-wash + soft radial
 * blur used during Making-Biscuits vision cutscenes.
 *
 * Per `docs/specs/SPEC-making-biscuits.md` §4 "Vision Delivery":
 *   Enter (~3s): chromatic blur + sepia desaturation over 1.5s.
 *   Body (~12-18s): steady desaturation, subtle chromatic shift.
 *   Exit (~2s): fade back through chromatic blur to normal.
 *
 * Implementation: a single full-screen pass attached to the scene's
 * active camera. The `strength` uniform in [0, 1] drives both the sepia
 * amount and the chromatic aberration offset. Callers animate it from
 * 0 → 1 during the vision and back to 0 on exit.
 *
 * The shader is registered once per module load and reused across every
 * VisionPostProcess instance. Disposal cleans up only the PostProcess
 * binding; the shader source remains cached in `Effect.ShadersStore`.
 */
import { Scene } from "@babylonjs/core/scene";
import { Camera } from "@babylonjs/core/Cameras/camera";
import { PostProcess } from "@babylonjs/core/PostProcesses/postProcess";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { VISION } from "../config/GameConfig";

/**
 * Fragment shader — sepia desaturation + red/blue channel splitting
 * (chromatic aberration) scaled by `strength`. Radial falloff gives the
 * center a clearer read than the edges, matching the "memory pinhole"
 * framing in the design doc.
 */
const VISION_FRAGMENT = `
varying vec2 vUV;
uniform sampler2D textureSampler;
uniform float strength;        // 0..1 — overall effect intensity
uniform float chromatic;       // max per-channel UV offset (in UV units)
uniform float sepiaStrength;   // 0..1 — sepia mix weight at full strength

void main(void) {
  vec2 centered = vUV - vec2(0.5, 0.5);
  float d = length(centered);

  // Radial falloff — edges warp harder than the center.
  float radial = smoothstep(0.05, 0.65, d);
  float s = strength * mix(0.4, 1.0, radial);

  // Chromatic aberration: sample R and B at radially-offset UVs.
  vec2 offset = normalize(centered + vec2(0.00001)) * chromatic * s;
  float r = texture2D(textureSampler, vUV + offset).r;
  float g = texture2D(textureSampler, vUV).g;
  float b = texture2D(textureSampler, vUV - offset).b;
  vec3 color = vec3(r, g, b);

  // Sepia mix — classic weighted tint.
  vec3 sepia = vec3(
    dot(color, vec3(0.393, 0.769, 0.189)),
    dot(color, vec3(0.349, 0.686, 0.168)),
    dot(color, vec3(0.272, 0.534, 0.131))
  );
  vec3 toned = mix(color, sepia, sepiaStrength * s);

  // Slight vignette darkening on top so the text reads louder.
  float vig = 1.0 - smoothstep(0.4, 0.95, d) * 0.45 * s;
  gl_FragColor = vec4(toned * vig, 1.0);
}
`;

if (!Effect.ShadersStore["visionPostFragmentShader"]) {
  Effect.ShadersStore["visionPostFragmentShader"] = VISION_FRAGMENT;
}

export class VisionPostProcess {
  /** Current effect strength [0..1]. Driven externally by VisionRenderer. */
  public strength = 0;

  private postProcess: PostProcess | null = null;
  private camera: Camera | null = null;

  /**
   * Attach the post-process to the scene's active camera.
   *
   * Fails silently on WebGL contexts that reject the shader — Vitest's
   * NullEngine is the primary example. `isAttached` surfaces success to
   * callers that want to skip animation when the effect isn't active.
   */
  constructor(scene: Scene) {
    const cam = scene.activeCamera;
    if (!cam) {
      // No camera yet (scene still bootstrapping) — skip attach.
      return;
    }
    this.camera = cam;
    try {
      this.postProcess = new PostProcess(
        "visionPostProcess",
        "visionPost",
        ["strength", "chromatic", "sepiaStrength"],
        null,
        1.0,
        cam,
        Texture.BILINEAR_SAMPLINGMODE,
        scene.getEngine(),
        false,
      );
      this.postProcess.onApply = (effect) => {
        effect.setFloat("strength", this.strength);
        // VISION.CHROMATIC_ABERRATION is in "arbitrary game units" —
        // scale to a reasonable UV offset (1 unit ≈ 0.004 UV).
        effect.setFloat("chromatic", VISION.CHROMATIC_ABERRATION * 0.004);
        effect.setFloat("sepiaStrength", VISION.SEPIA_STRENGTH);
      };
    } catch (err) {
      // Headless / non-WebGL context — keep `isAttached` false so the
      // VisionRenderer can still play the dialogue without the shader.
      this.postProcess = null;
      console.warn("[VisionPostProcess] shader unavailable:", err);
    }
  }

  /** True when the post-process is live on a camera. */
  get isAttached(): boolean {
    return this.postProcess !== null;
  }

  /**
   * Animate `strength` from its current value to `targetStrength` over
   * `durationMs`. Uses requestAnimationFrame so it tracks real time (we
   * don't want the effect to freeze with cutscene pause).
   */
  animateStrength(targetStrength: number, durationMs: number, onComplete?: () => void): void {
    const start = this.strength;
    const delta = targetStrength - start;
    if (Math.abs(delta) < 1e-4 || durationMs <= 0) {
      this.strength = targetStrength;
      onComplete?.();
      return;
    }
    const startTs = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - startTs) / durationMs);
      // Smoothstep easing so the chromatic wobble reads as a fluid
      // transition rather than a linear ramp.
      const eased = t * t * (3 - 2 * t);
      this.strength = start + delta * eased;
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        this.strength = targetStrength;
        onComplete?.();
      }
    };
    requestAnimationFrame(step);
  }

  /** Immediately clamp the strength to zero (used by skipAll / dispose). */
  reset(): void {
    this.strength = 0;
  }

  dispose(): void {
    if (this.postProcess && this.camera) {
      try {
        this.camera.detachPostProcess(this.postProcess);
      } catch {
        // Best-effort detach — some test harnesses lack detachPostProcess.
      }
      try {
        this.postProcess.dispose();
      } catch {
        // ignore
      }
    }
    this.postProcess = null;
    this.camera = null;
    this.strength = 0;
  }
}
